import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, Selection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { GapCursor } from '@tiptap/pm/gapcursor';

/**
 * Pagination: the Pages view (print layout) engine.
 *
 * It never changes the document. When enabled it measures the blocks of the editor and draws
 * widget decorations between blocks where a page ends, so the continuous ProseMirror element
 * looks like a stack of paper sheets. A `pageBreak` node always ends its page.
 *
 * Pages end between top-level blocks and, like Word and Google Docs, inside the wrappers that
 * hold whole stretches of a document: a generated section (`generatedField`, which wraps each
 * AI-written section of a generated document), a trust section and a blockquote are split
 * between their children (recursively), and a list (bullet, ordered, task) between its items
 * (an item stays whole, nested lists included). A wrapper is only split when its DOM holds its
 * children directly (its own DOM is its content DOM, or a node view's contentDOM reached through
 * only children with no box of their own) and is an ordinary block (see `contentHost`: normal
 * flow, visible overflow, no columns, no `break-inside: avoid`). Everything else (tables, code
 * blocks, images, diagrams, AI field cards, ...) moves whole. A widget inside a wrapper sits at
 * the position between two of its children, so it is a child of the wrapper's content element
 * (a `div` between two `li` of a list: list numbering continues, the browser keeps the list).
 * A page that ends before a wrapper's first child ends before the wrapper itself.
 *
 * ## API
 *
 *   import Pagination, { computePageLayout } from '../../extensions/Pagination';
 *
 *   editor.commands.setPagination({ enabled: true, paperSize: 'letter' });  // or 'a4'
 *   editor.commands.setPagination({ enabled: false });                      // back to continuous
 *   editor.commands.setPagination({ pageNumberFormat: 'n-of-m' });           // 'n' | 'n-of-m' | 'none'
 *   editor.commands.refreshPagination();   // force a full re-measure (e.g. after a CSS change)
 *   editor.storage.pagination  // { enabled, paperSize, pageCount }  (pageCount is 0 when off)
 *
 *   setPagination merges with the current settings; each option is optional. `paperSize` is
 *   'letter' (default) | 'a4' | { width, height } in CSS px. All of these are decoration-only
 *   transactions (addToHistory: false), so they never touch undo history or collaboration.
 *
 *   computePageLayout({ blocks, pageContentHeight, chromeHeight }) is the pure layout math
 *   (documented on the function) and is what the plugin runs on its measurements.
 *
 *   A click on a spacer or the tail puts the caret on the clicked page: in text where the page
 *   has some, otherwise (a blank page between two breaks, or after a final one) a gap cursor
 *   from `emptyPageCursor(doc, pos, dir, breakNodeName)`, where typing adds a paragraph.
 *
 *   Extension options (Pagination.configure): enabled (false), paperSize ('letter'),
 *   pageNumberFormat ('n'), gap (24, used when the sheet sets no --q-page-gap),
 *   breakNodeName ('pageBreak'), transparentNodes (PAGINATION_TRANSPARENT_NODES: wrappers split
 *   between their children), listNodes (PAGINATION_LIST_NODES: lists split between items).
 *
 *   flattenPageBlocks(tree) turns the measured block tree (wrappers with children) into the flat
 *   unit list computePageLayout places; both are pure and unit tested.
 *
 * ## Geometry contract with the host (EnhancedRichTextEditor in Pages view)
 *
 *   - The ProseMirror element is the sheet column: its padding is the page margin (top, right,
 *     bottom, left are read from its computed style, so responsive margins work). Page content
 *     height = paper height - padding-top - padding-bottom, so each sheet is exactly one paper
 *     height tall. The gap between sheets is read from the CSS variable --q-page-gap on the
 *     ProseMirror element (inherited), falling back to the `gap` option.
 *   - The element behind the sheet should be the canvas (var(--q-page-canvas)); the gap band
 *     paints that color across the full sheet width plus a few px (--q-page-bleed, default 6px)
 *     so the sheet shadow is hidden in the gap. Keep that much room around the sheet (canvas
 *     padding) so the bleed never creates a horizontal scrollbar.
 *   - The last sheet is padded to a full page by a tail widget, so the sheet always ends at a
 *     page boundary; a min-height of one paper height on the sheet covers the empty document.
 *
 * ## CSS class names and attributes (styles injected once into <head> as #quill-pagination-styles)
 *
 *   .ProseMirror.q-paginated            root class while enabled; also data-paper-size="letter|a4|custom"
 *   .q-page-spacer                      widget before the first block of a new page (or at the end
 *                                       of the doc after a final page break); data-page="N" is the
 *                                       page it ends. Height = unused space left on page N + bottom
 *                                       margin + gap + next page's top margin.
 *     .q-page-spacer__end               the rest of page N (transparent, the sheet shows through)
 *     .q-page-spacer__gap               the canvas-colored gap between the two sheets
 *     .q-page-spacer__start             the top margin of page N + 1 (transparent)
 *     .q-page-number                    "N" (or "Page N of M") centered in page N's bottom margin;
 *                                       the label is its data-number attribute shown as ::before
 *                                       content, so widgets add no text to the document DOM
 *   .q-page-tail                        widget after the last block: pads the last sheet to a full
 *     .q-page-number                    page and shows its number in the sheet's bottom padding
 *                                       (a negative margin-top when a final empty paragraph
 *                                       reaches into the bottom margin, see computePageLayout)
 *
 *   Both widgets also carry data-q-pagination="spacer|tail", data-q-key (decoration key),
 *   aria-hidden="true" and contenteditable="false". A spacer also has data-nested="true|false"
 *   (it sits inside a wrapper or list: its end and start bands, which always reach the sheet
 *   edges, are then painted with --q-page-sheet, so the wrapper's tint, border or review mark stops at the page
 *   end; the gap and the number are placed from the wrapper's horizontal offset), data-forced="true|false"
 *   (it follows a manual page break) and data-label="true" when the rest of that page shows a
 *   muted "Page break" label (::before of .q-page-spacer__end). The page break node itself is
 *   collapsed to zero height in Pages view by editorContentStyles (`.q-paginated .page-break`),
 *   as in print, so it can never be drawn into the bottom margin.
 *
 *   CSS variables read (all optional): --q-page-canvas, --q-page-number (number color),
 *   --q-page-gap, --q-page-bleed. Widgets are hidden when printing.
 *
 * ## Measurement (stable, cheap, no feedback loops)
 *
 *   Each block contributes its border-box height and its EFFECTIVE top/bottom margins (child
 *   margins that collapse through the block are included, see `effectiveMargin`). Adjacent
 *   margins are collapsed in the math, not read from positions, so the spacers (fixed-height
 *   blocks with no margins) can never change the next measurement. Reads are batched in one
 *   requestAnimationFrame after doc changes; results are cached per block DOM element and PM
 *   node (unchanged blocks are not re-read). A wrapper is never measured as a whole (its height
 *   includes the spacers inside it): only its own margins and insets and its children, so the
 *   widgets can never feed back into the measurement. A ResizeObserver on the sheet (width
 *   changes) and on every measured block (node views, Mermaid, images, previews growing), image
 *   `load` events and `document.fonts` re-measure only what changed. Identical results do nothing;
 *   height-only changes repaint the existing widget DOM in place (ProseMirror ignores mutations
 *   inside widgets) and only a change in where pages end dispatches a decoration transaction.
 *   Nothing runs while an IME composition is active.
 *   Where pages end comes from the math alone; the widget HEIGHTS are then snapped to the real
 *   layout (`alignToSheets`: where each widget sits relative to the one before, which no widget
 *   height changes), so sub-pixel margins never make sheets drift over a long document. Freshly
 *   drawn widgets are aligned by one follow-up frame that repaints in place.
 *
 * Widgets are contenteditable=false, aria-hidden, non-selectable, keyed by page number (so
 * ProseMirror reuses their DOM), ignore selection and stop all events except drag and drop.
 * The controller finds the live widget elements in the DOM on every measurement (by data-q-key),
 * so in-place repaints keep working after a plugin reconfigure recreates the plugin view.
 * Clicking the end of a page puts the caret at the end of that page's text; clicking the start
 * of the next page (or the gap) puts it at the start of the next page's text (a text caret is
 * preferred, so a page break or image next to the widget is never node-selected). The bands
 * reach the sheet edges, so the same holds for a click in a side margin beside a page end.
 */

export const paginationPluginKey = new PluginKey('quillPagination');

/** Paper sizes in CSS px at 96 px/in (match src/utils/paperSize.js). */
export const PAGINATION_PAPER_SIZES = Object.freeze({
  letter: Object.freeze({ id: 'letter', width: 816, height: 1056 }),
  a4: Object.freeze({ id: 'a4', width: 794, height: 1123 }),
});

/** Class names used by the widgets (see the header comment). */
export const PAGINATION_CLASSES = Object.freeze({
  root: 'q-paginated',
  spacer: 'q-page-spacer',
  end: 'q-page-spacer__end',
  gap: 'q-page-spacer__gap',
  start: 'q-page-spacer__start',
  number: 'q-page-number',
  tail: 'q-page-tail',
});

const STYLE_ID = 'quill-pagination-styles';
const PAGE_NUMBER_FORMATS = ['n', 'n-of-m', 'none'];
/** Sub-pixel tolerance so fractional measurements never push an exactly fitting block. */
const FIT_EPSILON = 0.5;
/** Smallest page content height the engine accepts (guards absurd paddings). */
const MIN_CONTENT_HEIGHT = 120;
/** Default page margin used when the sheet has no padding (px, 1 in). */
const DEFAULT_MARGIN = 96;
/** Max depth when following margins that collapse through nested blocks. */
const MARGIN_DEPTH = 4;
/** Room the rest of a page needs to show the "Page break" label after a manual break (px). */
const MIN_BREAK_LABEL_ROOM = 32;
/** Retry delay while an IME composition is active (ms). */
const COMPOSITION_RETRY_MS = 150;
/** Max wrapper nesting the engine descends into (deeper wrappers are measured whole). */
const MAX_NEST_DEPTH = 8;
/**
 * Wrapper blocks whose children the page may end between (when their DOM allows it, see
 * `contentHost`): generated-document sections, trust sections and blockquotes.
 */
export const PAGINATION_TRANSPARENT_NODES = Object.freeze(['generatedField', 'trustSection', 'blockquote']);
/** Lists: the page may end between their items (an item, nested lists included, stays whole). */
export const PAGINATION_LIST_NODES = Object.freeze(['bulletList', 'orderedList', 'taskList']);

/**
 * A finite, non-negative number (anything else becomes 0).
 * @param {*} value - Value.
 * @returns {number} Number.
 */
const size = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/**
 * A finite number (anything else becomes 0); margins may be negative.
 * @param {*} value - Value.
 * @returns {number} Number.
 */
const finite = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/**
 * CSS adjoining-margin collapsing of two margins.
 * @param {number} a - First margin (px).
 * @param {number} b - Second margin (px).
 * @returns {number} Collapsed margin (px).
 */
export function collapseMargins(a, b) {
  const x = finite(a);
  const y = finite(b);
  if (x >= 0 && y >= 0) return Math.max(x, y);
  if (x < 0 && y < 0) return Math.min(x, y);
  return x + y;
}

/**
 * Resolve a paper size option.
 * @param {'letter'|'a4'|{width: number, height: number}|undefined} paperSize - Option.
 * @returns {{id: string, width: number, height: number}} Paper (px).
 */
export function resolvePaper(paperSize) {
  if (paperSize && typeof paperSize === 'object' && size(paperSize.width) && size(paperSize.height)) {
    return { id: paperSize.id || 'custom', width: size(paperSize.width), height: size(paperSize.height) };
  }
  const id = String(paperSize || '').toLowerCase();
  return PAGINATION_PAPER_SIZES[id] || PAGINATION_PAPER_SIZES.letter;
}

/**
 * Pure page layout.
 *
 * Coordinates are page content coordinates (0 = top of the content area, below the top margin).
 * The rules follow how the browser prints the same HTML (the PDF export) and Word's
 * keep-with-next, so page ends match the exports:
 *   - A block fits when its border-box bottom is within the page content height. Blocks are
 *     never split: one that does not fit moves whole to the next page (the exports keep blocks
 *     together the same way, see enhancedExportService).
 *   - Blocks here are the units the page may end between: `flattenPageBlocks` turns transparent
 *     wrappers (generated sections, trust sections, blockquotes) and lists into their children
 *     first, so a long section or list is split between its paragraphs or items.
 *   - A heading (`keepWithNext`, several in a row count as one run) never ends a page: when the
 *     block after it does not fit, the heading moves to the next page with it, provided the
 *     heading is not the first block of its page and the two fit on a page together. When they
 *     do not, the block flows on its own as it would without the rule (no endless push), except
 *     a block taller than a page: that one moves with the heading and starts right below it,
 *     also when the heading starts the sheet, stretching that sheet (next rule). Headings that
 *     start a sheet never move (that would leave a blank page), so when the block after them
 *     fits a page but not below them, they stay alone on their page, as in Word with
 *     keep-with-next and keep-lines-together (blocks are never split by line).
 *   - At the top of a page after an automatic (unforced) page end, the block's top margin is
 *     dropped, as print does. The spacer before it is `shift` px shorter so the margin the block
 *     still has on screen lands exactly at the next page's content top. The first block of the
 *     document and the first block after a page break keep their top margin.
 *   - A page break block is always kept on its page (never pushed) and ends it (`forced`).
 *   - A block taller than a page starts on a fresh page (unless it already starts one, or follows
 *     the headings it is kept with) and
 *     stretches that sheet to as many whole pages as it covers, gaps included (no gaps are drawn
 *     inside it); its page number label shows the range. Following blocks fill the rest of the
 *     stretched sheet. One pass, bounded backtracking for headings, never loops.
 *   - The last block, when it is an empty paragraph (`trailingEmpty`: the paragraph the editor
 *     keeps after a generated section or table so there is somewhere to type), never opens a page
 *     of its own: when it does not fit it stays on the last page, reaching at most
 *     `trailingAllowance` px into the bottom margin (it shows nothing there but the caret). The
 *     last page's `lastPageFill` is then negative (the tail pulls up by that much, so the sheet
 *     still ends at a page boundary). Once it holds text it moves to a new page as usual.
 *
 * @param {object} input - Input.
 * @param {Array<{height: number, marginTop?: number, marginBottom?: number, isBreak?: boolean, keepWithNext?: boolean, trailingEmpty?: boolean}>} input.blocks
 *   Top-level blocks in order: border-box height and effective margins (px).
 * @param {number} input.pageContentHeight - Content height of one page (px).
 * @param {number} [input.chromeHeight=0] - Space a spacer adds besides the unused fill: bottom
 *   margin + gap + next page's top margin (px).
 * @param {number} [input.pageMarginTop] - The page's top margin (px): the most a dropped block
 *   margin can shorten a spacer by (defaults to chromeHeight).
 * @param {number} [input.trailingAllowance=0] - How far a final empty paragraph may reach into
 *   the bottom margin instead of opening a page of its own (px).
 * @returns {{
 *   spacers: Array<{beforeIndex: number, fill: number, height: number, pageNumberEnded: number,
 *     pageNumberStarted: number, forced: boolean, shift: number}>,
 *   pageCount: number,
 *   lastPageFill: number,
 *   lastSheetFirstPage: number,
 *   blockPages: number[],
 * }} `beforeIndex` is the block the spacer precedes (blocks.length = after the last block);
 *   `fill` is the unused content space left on the ended page below the previous block's bottom
 *   margin (may be slightly negative when only that margin overflows); `height` = max(0, fill +
 *   chromeHeight - shift); `pageNumberStarted` is the first page of the ended sheet (less than
 *   `pageNumberEnded` for a stretched sheet); `forced` is true after a page break; `shift` is the
 *   dropped top margin of the next block; `lastPageFill` is the unused space on the last page
 *   (for the tail pad); `blockPages[i]` is the page block i starts on.
 */
export function computePageLayout({ blocks, pageContentHeight, chromeHeight = 0, pageMarginTop, trailingAllowance = 0 } = {}) {
  const list = Array.isArray(blocks) ? blocks : [];
  const pageHeight = Number(pageContentHeight);
  if (!Number.isFinite(pageHeight) || pageHeight <= 0) {
    return { spacers: [], pageCount: 1, lastPageFill: 0, lastSheetFirstPage: 1, blockPages: list.map(() => 1) };
  }
  const chrome = size(chromeHeight);
  const allowance = size(trailingAllowance);
  const shiftLimit = pageMarginTop === undefined || pageMarginTop === null ? chrome : Math.min(size(pageMarginTop), chrome);
  const stride = pageHeight + chrome; // one page plus the chrome between two pages
  const spacers = [];
  const blockPages = [];

  let firstPage = 1; // number of the first page the current sheet covers
  let sheetPages = 1; // pages the current sheet covers (more than 1 when stretched by a tall block)
  let capacity = pageHeight; // content height of the current sheet
  let y = 0; // border-box bottom of the last block on the sheet
  let prevMarginBottom = 0;
  let placed = 0; // blocks on the current sheet
  let keepTopMargin = true; // first sheet, or the sheet after a page break
  let topShift = 0; // top margin dropped from the first block of the current sheet
  let history = []; // placement state before each block on the current sheet (heading backtracking)

  const lastPageOfSheet = () => firstPage + sheetPages - 1;
  const unused = () => capacity - y - (placed ? Math.max(0, prevMarginBottom) : 0);
  // Top of a block that starts a fresh sheet after an automatic page end (its top margin is
  // dropped by up to the page's top margin, see endSheet).
  const freshTop = (marginTop) => Math.max(0, Math.max(0, marginTop) - shiftLimit);
  const endSheet = (beforeIndex, forced) => {
    const next = list[beforeIndex];
    const shift = forced || !next ? 0 : Math.min(Math.max(0, finite(next.marginTop)), shiftLimit);
    const fill = unused();
    const ended = lastPageOfSheet();
    spacers.push({
      beforeIndex,
      fill,
      height: Math.max(0, fill + chrome - shift),
      pageNumberEnded: ended,
      pageNumberStarted: firstPage,
      forced,
      shift,
    });
    firstPage = ended + 1;
    sheetPages = 1;
    capacity = pageHeight;
    y = 0;
    prevMarginBottom = 0;
    placed = 0;
    keepTopMargin = forced;
    topShift = shift;
    history = [];
  };
  const sheetTop = (marginTop) => (keepTopMargin ? Math.max(0, marginTop) : Math.max(0, Math.max(0, marginTop) - topShift));

  let index = 0;
  while (index < list.length) {
    const block = list[index];
    const height = size(block && block.height);
    const marginTop = finite(block && block.marginTop);
    const marginBottom = finite(block && block.marginBottom);
    const isBreak = Boolean(block && block.isBreak);

    let top = placed ? y + collapseMargins(prevMarginBottom, marginTop) : sheetTop(marginTop);
    let flowsBelowHeadings = false;
    // A final empty paragraph that only just misses the page stays on it (no blank last page).
    const keepTrailing = Boolean(block && block.trailingEmpty) && index === list.length - 1 && placed > 0
      && !isBreak && top + height - capacity <= allowance + FIT_EPSILON;
    if (!isBreak && !keepTrailing && placed && top + height > capacity + FIT_EPSILON) {
      // Keep with next: the headings right before this block never end the page. They go to
      // the next page with it when they fit there together, or when the block is taller than a
      // page anyway (it then starts right below them). When they would not fit together on any
      // page, the block flows on its own (no endless push). Headings that already start the
      // sheet stay; a block taller than a page then follows them on the same (stretched) sheet.
      let first = history.length;
      while (first > 0 && history[first - 1].keepWithNext) first -= 1;
      const head = first < history.length ? history[first] : null;
      const tallAlone = freshTop(marginTop) + height > pageHeight + FIT_EPSILON;
      if (head && head.placedBefore > 0
        && (tallAlone || freshTop(head.marginTop) + (top + height - head.top) <= pageHeight + FIT_EPSILON)) {
        y = head.yBefore;
        prevMarginBottom = head.marginBottomBefore;
        placed = head.placedBefore;
        blockPages.length = head.index;
        endSheet(head.index, false);
        index = head.index;
        continue;
      }
      flowsBelowHeadings = Boolean(head && head.placedBefore === 0 && tallAlone);
      if (!flowsBelowHeadings) {
        endSheet(index, false);
        top = sheetTop(marginTop);
      }
    }
    const bottom = top + height;
    if ((!placed || flowsBelowHeadings) && bottom > capacity + FIT_EPSILON) {
      // Taller than a page (at the top of a sheet, or right below the headings it is kept
      // with): let it overflow, as whole pages (with gaps).
      sheetPages = Math.max(sheetPages, Math.ceil((bottom - FIT_EPSILON + chrome) / stride));
      capacity = sheetPages * pageHeight + (sheetPages - 1) * chrome;
    }
    history.push({
      index,
      top,
      marginTop,
      yBefore: y,
      marginBottomBefore: prevMarginBottom,
      placedBefore: placed,
      keepWithNext: Boolean(block && block.keepWithNext) && !isBreak,
    });
    blockPages.push(firstPage + Math.min(Math.floor(Math.max(0, top) / stride), sheetPages - 1));
    y = bottom;
    prevMarginBottom = marginBottom;
    placed += 1;
    if (isBreak) endSheet(index + 1, true);
    index += 1;
  }

  return { spacers, pageCount: lastPageOfSheet(), lastPageFill: unused(), lastSheetFirstPage: firstPage, blockPages };
}

/**
 * Flatten a measured block tree into the units `computePageLayout` places (pure).
 *
 * A wrapper (a node with `children`) is replaced by its children, recursively, with its own box
 * folded into the first and last of them the way CSS lays it out:
 *   - its top margin collapses with the first child's when nothing separates them
 *     (`collapseTop`, default: no `insetTop`); otherwise the inset (padding + border) and the
 *     child's margin become part of the first child's height and the wrapper's margin is the
 *     first child's outer margin. The same at the bottom with the last child.
 *   - the first child takes the wrapper's `at`: a page that ends right before a wrapper ends
 *     before the wrapper itself (outside it), so the spacer never sits inside the wrapper as its
 *     first child. Every other child keeps its own `at` (a position between two children).
 *   - `extraBefore` (in-flow elements other plugins put before the node) adds to the height of
 *     its first unit.
 * A wrapper with no children is a plain block of its own `height`.
 *
 * @param {Array<object>} tree - Nodes: leaves `{height, marginTop, marginBottom, isBreak,
 *   keepWithNext, trailingEmpty, at, extraBefore}` or wrappers `{children, marginTop, marginBottom, insetTop,
 *   insetBottom, collapseTop, collapseBottom, at, extraBefore}` (px; `at` is opaque).
 * @returns {Array<{height: number, marginTop: number, marginBottom: number, isBreak: boolean,
 *   keepWithNext: boolean, trailingEmpty: boolean, at: *}>} Units in document order, ready for computePageLayout.
 */
export function flattenPageBlocks(tree) {
  const out = [];
  const flatten = (node, depth) => {
    const units = [];
    const kids = node && Array.isArray(node.children) ? node.children : null;
    if (!kids || !kids.length || depth > MAX_NEST_DEPTH) {
      units.push({
        height: size(node && node.height),
        marginTop: finite(node && node.marginTop),
        marginBottom: finite(node && node.marginBottom),
        isBreak: Boolean(node && node.isBreak),
        keepWithNext: Boolean(node && node.keepWithNext),
        trailingEmpty: Boolean(node && node.trailingEmpty),
        at: node ? node.at : undefined,
      });
    } else {
      kids.forEach((kid) => {
        flatten(kid, depth + 1).forEach((unit) => units.push(unit));
      });
      const first = units[0];
      const last = units[units.length - 1];
      const insetTop = size(node.insetTop);
      const insetBottom = size(node.insetBottom);
      const collapseTop = node.collapseTop === undefined ? insetTop === 0 : Boolean(node.collapseTop);
      const collapseBottom = node.collapseBottom === undefined ? insetBottom === 0 : Boolean(node.collapseBottom);
      if (collapseTop) {
        first.marginTop = collapseMargins(node.marginTop, first.marginTop);
      } else {
        first.height = size(first.height + insetTop + first.marginTop);
        first.marginTop = finite(node.marginTop);
      }
      if (collapseBottom) {
        last.marginBottom = collapseMargins(last.marginBottom, node.marginBottom);
      } else {
        last.height = size(last.height + last.marginBottom + insetBottom);
        last.marginBottom = finite(node.marginBottom);
      }
      first.at = node.at;
    }
    if (node && size(node.extraBefore)) units[0].height += size(node.extraBefore);
    return units;
  };
  (Array.isArray(tree) ? tree : []).forEach((node) => {
    flatten(node, 0).forEach((unit) => out.push(unit));
  });
  return out;
}

/* ------------------------------------------------------------------------------------------ */
/* DOM measurement                                                                              */
/* ------------------------------------------------------------------------------------------ */

/**
 * Computed style of an element (null outside a browser).
 * @param {Element} el - Element.
 * @returns {CSSStyleDeclaration|null} Style.
 */
const styleOf = (el) => {
  try {
    const win = el && el.ownerDocument && el.ownerDocument.defaultView;
    return win && typeof win.getComputedStyle === 'function' ? win.getComputedStyle(el) : null;
  } catch {
    return null;
  }
};

const px = (value) => finite(parseFloat(value));

/**
 * Whether an element is taken out of normal flow (or not rendered).
 * @param {CSSStyleDeclaration|null} style - Computed style.
 * @returns {boolean} True when it takes no space in the flow.
 */
const outOfFlow = (style) => Boolean(style) && (
  style.display === 'none'
  || style.position === 'absolute'
  || style.position === 'fixed'
  || (style.float && style.float !== 'none')
);

/**
 * Whether a child's margin on `side` collapses through this element (CSS 2.1 8.3.1): a plain
 * block box with no padding or border on that side and visible overflow.
 * @param {CSSStyleDeclaration|null} style - Computed style.
 * @param {'top'|'bottom'} side - Side.
 * @returns {boolean} True when it collapses through.
 */
const collapsesThrough = (style, side) => {
  if (!style) return false;
  if (style.display !== 'block' && style.display !== 'list-item') return false;
  const visible = (v) => !v || v === 'visible' || v === 'clip';
  if (!visible(style.overflow) || !visible(style.overflowX) || !visible(style.overflowY)) return false;
  const Side = side === 'top' ? 'Top' : 'Bottom';
  if (px(style[`padding${Side}`]) !== 0) return false;
  if (px(style[`border${Side}Width`]) !== 0 && style[`border${Side}Style`] && style[`border${Side}Style`] !== 'none') return false;
  return true;
};

/**
 * First (or last) in-flow child element whose margin can adjoin the parent's, or null when
 * text or inline content sits there (inline boxes never collapse with the parent).
 * @param {Element} el - Parent.
 * @param {'top'|'bottom'} side - Side.
 * @returns {Element|null} Child.
 */
const edgeChild = (el, side) => {
  const nodes = Array.from(el.childNodes || []);
  if (side === 'bottom') nodes.reverse();
  for (const node of nodes) {
    if (node.nodeType === 3) {
      if (/\S/.test(node.nodeValue || '')) return null;
      continue;
    }
    if (node.nodeType !== 1) continue;
    const style = styleOf(node);
    if (outOfFlow(style)) continue;
    if (!style || (style.display !== 'block' && style.display !== 'list-item' && style.display !== 'flow-root'
      && style.display !== 'flex' && style.display !== 'grid' && style.display !== 'table')) return null;
    return node;
  }
  return null;
};

/**
 * Effective outer margin of a block on one side: its own margin collapsed with the margins of
 * nested first/last children that collapse through it.
 * @param {Element} el - Block element.
 * @param {'top'|'bottom'} side - Side.
 * @param {number} [depth=0] - Recursion depth.
 * @returns {number} Margin (px).
 */
export function effectiveMargin(el, side, depth = 0) {
  const style = styleOf(el);
  if (!style) return 0;
  const own = px(side === 'top' ? style.marginTop : style.marginBottom);
  if (depth >= MARGIN_DEPTH || !collapsesThrough(style, side)) return own;
  const child = edgeChild(el, side);
  return child ? collapseMargins(own, effectiveMargin(child, side, depth + 1)) : own;
}

/**
 * Measure one top-level block.
 * @param {Node|null} dom - Block DOM.
 * @returns {{height: number, marginTop: number, marginBottom: number}} Box.
 */
export function measureBlock(dom) {
  if (!dom || dom.nodeType !== 1 || typeof dom.getBoundingClientRect !== 'function') {
    return { height: 0, marginTop: 0, marginBottom: 0 };
  }
  const style = styleOf(dom);
  if (outOfFlow(style)) return { height: 0, marginTop: 0, marginBottom: 0 };
  const rect = dom.getBoundingClientRect();
  return {
    height: size(rect && rect.height),
    marginTop: effectiveMargin(dom, 'top'),
    marginBottom: effectiveMargin(dom, 'bottom'),
  };
}

/**
 * Whether a computed style allows the page to end between the element's children: an ordinary
 * block in normal flow with visible overflow, no columns, and no `break-inside: avoid`.
 * @param {CSSStyleDeclaration|null} style - Computed style.
 * @returns {boolean} True when it can be split.
 */
const splittableStyle = (style) => {
  if (!style || outOfFlow(style)) return false;
  if (style.display !== 'block' && style.display !== 'list-item') return false;
  const visible = (v) => !v || v === 'visible' || v === 'clip';
  if (!visible(style.overflow) || !visible(style.overflowX) || !visible(style.overflowY)) return false;
  const columns = style.columnCount;
  if (columns && columns !== 'auto' && columns !== '1') return false;
  const avoid = (v) => v === 'avoid' || v === 'avoid-page';
  return !avoid(style.breakInside) && !avoid(style.pageBreakInside);
};

/**
 * The element that holds a wrapper node's children, when the page may end between them: the
 * node's own DOM when it is its content DOM, or a node view's contentDOM reached through only
 * children that add no box of their own (no margin, padding or border on top or bottom).
 * @param {object} desc - ProseMirror view desc of the node.
 * @returns {HTMLElement|null} Content element, or null to measure the node whole.
 */
export const contentHost = (desc) => {
  if (!desc || !desc.dom || desc.dom.nodeType !== 1 || !desc.contentDOM || desc.contentDOM.nodeType !== 1) return null;
  if (!splittableStyle(styleOf(desc.dom))) return null;
  for (let el = desc.contentDOM; el !== desc.dom; el = el.parentElement) {
    const parent = el.parentElement;
    if (!parent || !desc.dom.contains(parent) || parent.childElementCount !== 1) return null;
    const style = styleOf(el);
    if (!splittableStyle(style) || !collapsesThrough(style, 'top') || !collapsesThrough(style, 'bottom')
      || px(style.marginTop) !== 0 || px(style.marginBottom) !== 0) return null;
  }
  return desc.contentDOM;
};

/**
 * Box of a wrapper for `flattenPageBlocks`: its own margins and its top/bottom insets.
 * @param {HTMLElement} el - Wrapper element.
 * @returns {{marginTop: number, marginBottom: number, insetTop: number, insetBottom: number,
 *   collapseTop: boolean, collapseBottom: boolean}} Box.
 */
const wrapperBox = (el) => {
  const style = styleOf(el);
  const inset = (Side) => px(style && style[`padding${Side}`])
    + (style && style[`border${Side}Style`] && style[`border${Side}Style`] !== 'none' ? px(style[`border${Side}Width`]) : 0);
  return {
    marginTop: px(style && style.marginTop),
    marginBottom: px(style && style.marginBottom),
    insetTop: inset('Top'),
    insetBottom: inset('Bottom'),
    collapseTop: collapsesThrough(style, 'top'),
    collapseBottom: collapsesThrough(style, 'bottom'),
  };
};

/**
 * Inject the widget styles once per document.
 * @param {Document} doc - Document.
 * @returns {void}
 */
const ensureStyles = (doc) => {
  if (!doc || !doc.head || doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = [
    '.q-page-spacer,.q-page-tail{position:relative;display:block;box-sizing:border-box;margin:0;padding:0;border:0;',
    '-webkit-user-select:none;user-select:none;cursor:text;}',
    // The bands reach the sheet edges (see paintWidget) and take the clicks there, so a click in
    // a side margin beside a page end lands on that page like one in the text column.
    '.q-page-spacer__end,.q-page-spacer__start{position:absolute;left:0;right:0;}',
    '.q-page-spacer__end{top:0;}.q-page-spacer__start{bottom:0;}',
    '.q-page-spacer__gap{position:absolute;background:var(--q-page-canvas,var(--q-canvas,#EEECE7));}',
    // The soft sheet edges (the end of one sheet, the start of the next) only span the sheet
    // width; the band itself bleeds past the sides to hide the sheet shadow.
    '.q-page-spacer__gap::before{content:"";position:absolute;top:0;bottom:0;left:var(--q-page-bleed,6px);',
    'right:var(--q-page-bleed,6px);box-shadow:inset 0 7px 6px -7px rgba(0,0,0,.2),inset 0 -7px 6px -7px rgba(0,0,0,.2);}',
    // After a manual page break the rest of the page says so (the break itself has no height in
    // Pages view, like in print), when there is room for the label.
    '.q-page-spacer[data-forced="true"][data-label="true"] .q-page-spacer__end::before{content:"Page break";',
    'position:absolute;top:10px;left:0;right:0;text-align:center;',
    'font-family:var(--q-font-sans,system-ui,-apple-system,"Segoe UI",sans-serif);font-size:11px;font-weight:500;',
    'letter-spacing:.02em;line-height:1;color:var(--q-page-number,var(--q-faint,#716C65));}',
    '.q-page-number{position:absolute;left:0;right:0;display:flex;align-items:center;justify-content:center;',
    'pointer-events:none;font-family:var(--q-font-sans,system-ui,-apple-system,"Segoe UI",sans-serif);font-size:11px;',
    'font-weight:400;font-style:normal;text-transform:none;text-decoration:none;',
    'line-height:1;letter-spacing:.02em;font-variant-numeric:tabular-nums;color:var(--q-page-number,var(--q-faint,#716C65));}',
    // The label is generated content, so it never adds text to the document DOM (a spacer inside
    // a generated section must not change that section's textContent).
    '.q-page-number::before{content:attr(data-number);}',
    // A spacer inside a wrapper (generated section, blockquote, list) paints the margins it spans
    // with the sheet color across the whole sheet width, so the wrapper's tint, border or review
    // mark stops at the page end like on paper.
    '.q-page-spacer[data-nested="true"] .q-page-spacer__end,.q-page-spacer[data-nested="true"] .q-page-spacer__start{',
    'background:var(--q-page-sheet,transparent);}',
    // The gap cursor on a blank page (see emptyPageCursor) follows the text color, so it stays
    // visible on a dark sheet.
    '.q-paginated .ProseMirror-gapcursor:after{border-top-color:currentColor;}',
    '@media print{.q-page-spacer,.q-page-tail{display:none!important;}}',
  ].join('');
  doc.head.appendChild(style);
};

/**
 * Page number label. A sheet stretched by a block taller than a page covers several pages and
 * shows their range ("2-4", "Pages 2-4 of 5").
 * @param {number} page - Page (the last page of the sheet).
 * @param {number} total - Pages.
 * @param {string} format - 'n' | 'n-of-m' | 'none'.
 * @param {number} [firstPage] - First page of the sheet (defaults to `page`).
 * @returns {string} Label.
 */
export const formatPageNumber = (page, total, format, firstPage = page) => {
  if (format === 'none') return '';
  const range = Number.isFinite(firstPage) && firstPage < page;
  if (format === 'n-of-m') return range ? `Pages ${firstPage}-${page} of ${total}` : `Page ${page} of ${total}`;
  return range ? `${firstPage}-${page}` : String(page);
};

/**
 * Normalize pagination settings.
 * @param {object} prev - Current settings.
 * @param {object} patch - Changes.
 * @returns {{enabled: boolean, paperSize: *, pageNumberFormat: string}} Settings.
 */
const normalizeConfig = (prev, patch = {}) => {
  const next = { ...prev };
  if (patch && 'enabled' in patch) next.enabled = Boolean(patch.enabled);
  if (patch && patch.paperSize) next.paperSize = patch.paperSize;
  if (patch && PAGE_NUMBER_FORMATS.includes(patch.pageNumberFormat)) next.pageNumberFormat = patch.pageNumberFormat;
  return next;
};

/**
 * Whether two configs ask for the same user-facing settings (ignores the refresh counter).
 * @param {object} a - Config.
 * @param {object} b - Config.
 * @returns {boolean} True when equal.
 */
const sameSettings = (a, b) => a.enabled === b.enabled
  && resolvePaper(a.paperSize).width === resolvePaper(b.paperSize).width
  && resolvePaper(a.paperSize).height === resolvePaper(b.paperSize).height
  && a.pageNumberFormat === b.pageNumberFormat;

const sameConfig = (a, b) => a.enabled === b.enabled
  && resolvePaper(a.paperSize).width === resolvePaper(b.paperSize).width
  && resolvePaper(a.paperSize).height === resolvePaper(b.paperSize).height
  && a.pageNumberFormat === b.pageNumberFormat
  && a.refresh === b.refresh;

/* ------------------------------------------------------------------------------------------ */
/* Widgets                                                                                      */
/* ------------------------------------------------------------------------------------------ */

/** Attributes every widget root carries: its kind and its decoration key. */
const WIDGET_ATTR = 'data-q-pagination';
const WIDGET_KEY_ATTR = 'data-q-key';

const DRAG_EVENTS = new Set(['dragstart', 'dragenter', 'dragover', 'dragleave', 'drop', 'dragend']);

/**
 * Create a child element with a class.
 * @param {HTMLElement} parent - Parent.
 * @param {string} className - Class.
 * @returns {HTMLElement} Child.
 */
const child = (parent, className) => {
  const el = parent.ownerDocument.createElement('div');
  el.className = className;
  parent.appendChild(el);
  return el;
};

/**
 * Write widget geometry into its DOM (skipped when nothing changed). ProseMirror ignores these
 * mutations because they happen inside a widget.
 * @param {HTMLElement} el - Widget root.
 * @param {object} data - Widget data.
 * @returns {void}
 */
const paintWidget = (el, data) => {
  if (!el || !data) return;
  const signature = JSON.stringify(data);
  if (el.qPaginationSignature === signature) return;
  el.qPaginationSignature = signature;
  const label = formatPageNumber(data.page, data.total, data.format, data.firstPage);
  el.style.height = `${Math.max(0, data.height)}px`;
  el.setAttribute('data-page', String(data.page));
  // Horizontal geometry: `insetLeft/Right` is how far the widget's own box sits from the sheet
  // edges (the page margins at the top level; more inside a wrapper or list).
  const insetLeft = finite(data.insetLeft === undefined ? data.marginLeft : data.insetLeft);
  const insetRight = finite(data.insetRight === undefined ? data.marginRight : data.insetRight);
  const nested = Boolean(data.nested);
  el.setAttribute('data-nested', nested ? 'true' : 'false');
  const number = el.querySelector(`.${PAGINATION_CLASSES.number}`);
  if (number) {
    number.setAttribute('data-number', label);
    number.style.display = label ? '' : 'none';
    number.style.height = `${data.marginBottom}px`;
    // Centered on the page's text column, wherever the widget sits.
    number.style.left = `${finite(data.marginLeft) - insetLeft}px`;
    number.style.right = `${finite(data.marginRight) - insetRight}px`;
  }
  if (data.kind === 'tail') {
    if (number) number.style.top = '100%';
    // A last page that runs a little over (a final empty paragraph reaching into the bottom
    // margin, see computePageLayout) pulls the tail up, so the sheet still ends on the page.
    const fill = finite(data.fill === undefined ? data.height : data.fill);
    el.style.height = `${Math.max(0, fill)}px`;
    el.style.marginTop = fill < 0 ? `${fill}px` : '0px';
    return;
  }
  const end = el.querySelector(`.${PAGINATION_CLASSES.end}`);
  const gap = el.querySelector(`.${PAGINATION_CLASSES.gap}`);
  const start = el.querySelector(`.${PAGINATION_CLASSES.start}`);
  // The next block's dropped top margin (`shift`) is drawn by the block itself, below the spacer.
  const startHeight = Math.max(0, data.marginTop - (data.shift || 0));
  const endHeight = Math.max(0, data.height - startHeight - data.gap);
  el.setAttribute('data-forced', data.forced ? 'true' : 'false');
  el.setAttribute('data-label', data.forced && data.fill >= MIN_BREAK_LABEL_ROOM ? 'true' : 'false');
  // The end and start bands reach the sheet edges, so clicks in the side margins land on them
  // (nested ones are also sheet colored, see the styles).
  [end, start].forEach((band) => {
    if (!band) return;
    band.style.left = `${-insetLeft}px`;
    band.style.right = `${-insetRight}px`;
  });
  if (end) end.style.height = `${endHeight}px`;
  if (start) start.style.height = `${startHeight}px`;
  if (gap) {
    gap.style.bottom = `${startHeight}px`;
    gap.style.height = `${data.gap}px`;
    gap.style.left = `calc(${-insetLeft}px - var(--q-page-bleed, 6px))`;
    gap.style.right = `calc(${-insetRight}px - var(--q-page-bleed, 6px))`;
  }
  if (number) number.style.bottom = `${startHeight + data.gap}px`;
};

/** Most a measured widget height may differ from the math before it is left as computed (px). */
const ALIGN_TOLERANCE = 2;
/** Widget heights snap to the layout grid browsers use (1/64 px), so rounding never adds up. */
const LAYOUT_UNIT = 64;

/**
 * Snap the widget heights of a layout to the real layout, in place (`data.height` of each
 * widget, and the tail's `fill`). The math sums measured heights and computed margins, while the
 * browser lays margins out on a 1/64 px grid, so a page of many paragraphs can end a tenth of a
 * pixel off, which adds up over a long document. The content between two widgets lays out the
 * same whatever their heights (a widget is a block with a height and no margins), so this reads
 * where each widget starts relative to the one before and sizes it so every sheet ends exactly on
 * its page boundary (absolute targets, so nothing accumulates). It only reads, never moves a page
 * end, and stops at the first widget it cannot place or whose correction is over ALIGN_TOLERANCE
 * px (that one and the rest keep the math), so it cannot feed back.
 * @param {Array<{key: string, kind: string, data: object}>} widgets - Spacers in page order, then the tail.
 * @param {Element[]} widgetEls - Widget elements currently in the DOM.
 * @param {{dom: HTMLElement, paperHeight: number, gap: number, marginTop: number, marginBottom: number}} geometry - Sheet geometry.
 * @returns {number} How many widgets were aligned.
 */
export function alignToSheets(widgets, widgetEls, { dom, paperHeight, gap, marginTop, marginBottom }) {
  if (!dom || typeof dom.getBoundingClientRect !== 'function' || !widgetEls || !widgetEls.length) return 0;
  const rects = new Map();
  widgetEls.forEach((el) => {
    const key = el.getAttribute(WIDGET_KEY_ATTR);
    if (!key || rects.has(key)) return;
    const rect = el.getBoundingClientRect();
    rects.set(key, { top: rect.top, bottom: rect.bottom, marginTop: px(el.style.marginTop) });
  });
  const rootRect = dom.getBoundingClientRect();
  if (!rootRect || !(rootRect.height > 0)) return 0;
  const snap = (value) => Math.round(value * LAYOUT_UNIT) / LAYOUT_UNIT;
  // `measuredBottom` is where the previous widget (or the top padding) ends now, `plannedBottom`
  // where it will end with the new heights; the content in between keeps its own height.
  let measuredBottom = rootRect.top + finite(dom.clientTop) + marginTop;
  let plannedBottom = measuredBottom;
  let sheetTop = rootRect.top; // target top of the current sheet
  let aligned = 0;
  for (let i = 0; i < widgets.length; i += 1) {
    const { key, kind, data } = widgets[i];
    const rect = rects.get(key);
    if (!rect || !data) return aligned;
    const pages = Math.max(1, data.page - (data.firstPage || data.page) + 1);
    const sheetBottom = sheetTop + pages * paperHeight + (pages - 1) * gap;
    if (kind === 'tail') {
      // The tail's top without the margin it has now, then the fill up to the bottom margin.
      const top = plannedBottom + (rect.top - rect.marginTop - measuredBottom);
      const fill = snap(sheetBottom - marginBottom - top);
      const model = finite(data.fill === undefined ? data.height : data.fill);
      if (!Number.isFinite(fill) || Math.abs(fill - model) > ALIGN_TOLERANCE) return aligned;
      data.fill = fill;
      data.height = Math.max(0, fill);
      return aligned + 1;
    }
    const top = plannedBottom + (rect.top - measuredBottom);
    const startHeight = Math.max(0, marginTop - (data.shift || 0));
    const height = snap(sheetBottom + gap + startHeight - top);
    if (!(height >= 0) || Math.abs(height - data.height) > ALIGN_TOLERANCE) return aligned;
    data.height = height;
    measuredBottom = rect.bottom;
    plannedBottom = top + height;
    sheetTop = sheetBottom + gap;
    aligned += 1;
  }
  return aligned;
}

/* ------------------------------------------------------------------------------------------ */
/* Controller (plugin view)                                                                     */
/* ------------------------------------------------------------------------------------------ */

const requestFrame = (cb) => (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
  ? { raf: window.requestAnimationFrame(cb) }
  : { timeout: setTimeout(cb, 16) });

const cancelFrame = (handle) => {
  if (!handle) return;
  if (handle.raf !== undefined && typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
    window.cancelAnimationFrame(handle.raf);
  }
  if (handle.timeout !== undefined) clearTimeout(handle.timeout);
};

/**
 * Measures the view and keeps the widget decorations in sync. One per editor view.
 */
class PaginationController {
  /**
   * @param {import('@tiptap/pm/view').EditorView} view - View.
   * @param {object} shared - Per-editor shared state ({ data, storage, options, committed }).
   */
  constructor(view, shared) {
    this.view = view;
    this.shared = shared;
    this.frame = null;
    this.retry = null;
    this.attached = false;
    this.cache = new WeakMap();
    this.wrapCache = new WeakMap();
    this.observed = new Set();
    this.width = null;
    this.positionSignature = '';
    this.fromDoc = false;
    this.dispatching = false;
    this.aligning = false; // a follow-up frame is aligning freshly drawn widgets
    this.childCount = -1;
    this.onLoad = this.onLoad.bind(this);
    this.onFonts = this.onFonts.bind(this);
    this.onWindowResize = this.onWindowResize.bind(this);
    const state = paginationPluginKey.getState(view.state);
    if (state && state.config.enabled) this.attach();
    this.restoreCommitted(state);
  }

  /**
   * A restored older EditorState (e.g. `savedState.reconfigure()` when an AI review is
   * finished) brings back the plugin state it had then. If the settings were changed since,
   * re-apply the last committed ones (outside the current update cycle).
   * @param {object|undefined} state - Plugin state of the view's state.
   * @returns {void}
   */
  restoreCommitted(state) {
    const { committed } = this.shared;
    if (!state || !committed || sameSettings(state.config, committed)) return;
    const settings = { enabled: committed.enabled, paperSize: committed.paperSize, pageNumberFormat: committed.pageNumberFormat };
    Promise.resolve().then(() => {
      const { view } = this;
      if (!view || view.isDestroyed || this.destroyed) return;
      const current = paginationPluginKey.getState(view.state);
      if (!current || sameSettings(current.config, settings)) return;
      view.dispatch(view.state.tr.setMeta(paginationPluginKey, { config: settings }).setMeta('addToHistory', false));
    });
  }

  /** @returns {object} Current plugin state. */
  get pluginState() {
    return paginationPluginKey.getState(this.view.state);
  }

  /**
   * Plugin view update.
   * @param {import('@tiptap/pm/view').EditorView} view - View.
   * @param {import('@tiptap/pm/state').EditorState} prevState - Previous state.
   * @returns {void}
   */
  update(view, prevState) {
    this.view = view;
    const next = this.pluginState;
    const prev = paginationPluginKey.getState(prevState);
    if (!next) return;
    if (!prev || !sameConfig(prev.config, next.config)) {
      this.syncStorage(next.config);
      if (next.config.enabled) {
        this.attach();
        this.invalidateAll();
        this.schedule();
      } else {
        this.detach();
      }
      return;
    }
    if (!next.config.enabled || this.dispatching) return;
    if (view.state.doc !== prevState.doc) this.schedule({ fromDoc: true });
    // Another plugin added or removed an in-flow widget between blocks (no doc change).
    else if (view.dom.childElementCount !== this.childCount) this.schedule();
  }

  /**
   * Mirror the settings into editor.storage.pagination.
   * @param {object} config - Settings.
   * @returns {void}
   */
  syncStorage(config) {
    this.shared.committed = {
      enabled: config.enabled,
      paperSize: config.paperSize,
      pageNumberFormat: config.pageNumberFormat,
    };
    const { storage } = this.shared;
    if (!storage) return;
    storage.enabled = config.enabled;
    storage.paperSize = resolvePaper(config.paperSize).id;
    if (!config.enabled) storage.pageCount = 0;
  }

  /** Start observing. @returns {void} */
  attach() {
    if (this.attached) return;
    this.attached = true;
    const { dom } = this.view;
    const doc = dom.ownerDocument;
    ensureStyles(doc);
    this.syncStorage(this.pluginState.config);
    dom.addEventListener('load', this.onLoad, true);
    const win = doc && doc.defaultView;
    if (win && typeof win.ResizeObserver === 'function') {
      this.resizeObserver = new win.ResizeObserver((entries) => this.onResize(entries));
      this.resizeObserver.observe(dom);
    } else if (win) {
      win.addEventListener('resize', this.onWindowResize);
    }
    const fonts = doc && doc.fonts;
    if (fonts) {
      if (fonts.ready && typeof fonts.ready.then === 'function') fonts.ready.then(this.onFonts, () => {});
      if (typeof fonts.addEventListener === 'function') fonts.addEventListener('loadingdone', this.onFonts);
    }
    this.schedule();
  }

  /** Stop observing and forget measurements. @returns {void} */
  detach() {
    if (!this.attached) return;
    this.attached = false;
    const { dom } = this.view;
    const doc = dom.ownerDocument;
    dom.removeEventListener('load', this.onLoad, true);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.resizeObserver = null;
    if (doc && doc.defaultView) doc.defaultView.removeEventListener('resize', this.onWindowResize);
    if (doc && doc.fonts && typeof doc.fonts.removeEventListener === 'function') {
      doc.fonts.removeEventListener('loadingdone', this.onFonts);
    }
    cancelFrame(this.frame);
    this.frame = null;
    clearTimeout(this.retry);
    this.retry = null;
    this.observed.clear();
    this.cache = new WeakMap();
    this.wrapCache = new WeakMap();
    this.width = null;
    this.positionSignature = '';
    this.childCount = -1;
    this.shared.data.clear();
  }

  /** Forget every cached block measurement. @returns {void} */
  invalidateAll() {
    this.cache = new WeakMap();
    this.wrapCache = new WeakMap();
  }

  /**
   * ResizeObserver callback.
   * @param {ResizeObserverEntry[]} entries - Entries.
   * @returns {void}
   */
  onResize(entries) {
    let changed = false;
    entries.forEach((entry) => {
      if (entry.target === this.view.dom) {
        // Same measure as measure() stores (clientWidth includes the page margins), so a
        // height-only change (a page added while typing) keeps every cached block measurement.
        const width = entry.target.clientWidth;
        if (width !== this.width) {
          this.invalidateAll();
          changed = true;
        }
      } else {
        this.cache.delete(entry.target);
        changed = true;
      }
    });
    if (changed) this.schedule();
  }

  /** Window resize fallback (no ResizeObserver). @returns {void} */
  onWindowResize() {
    this.invalidateAll();
    this.schedule();
  }

  /**
   * Capture-phase `load` (images) inside the editor.
   * @param {Event} event - Event.
   * @returns {void}
   */
  onLoad(event) {
    // Forget the measured block holding the image (and any wrapper around it).
    const root = this.view.dom;
    let current = event.target;
    let inside = false;
    while (current && current !== root) {
      if (current.nodeType === 1) {
        this.cache.delete(current);
        this.wrapCache.delete(current);
      }
      current = current.parentNode;
      if (current === root) inside = true;
    }
    if (!inside) this.invalidateAll();
    this.schedule();
  }

  /** Fonts finished loading. @returns {void} */
  onFonts() {
    if (!this.attached) return;
    this.invalidateAll();
    this.schedule();
  }

  /**
   * The direct child of `container` that contains `node`.
   * @param {Node} node - Node.
   * @param {Element} [container] - Container (the editor element by default).
   * @returns {Element|null} Child element.
   */
  childAncestor(node, container = this.view.dom) {
    let current = node;
    while (current && current.parentNode && current.parentNode !== container) current = current.parentNode;
    return current && current.parentNode === container ? current : null;
  }

  /**
   * Measure the children of one container (the editor element, or the content element of a
   * wrapper or list) into block tree nodes for `flattenPageBlocks`. One in-order walk over the
   * container's DOM children (view.nodeDOM scans from the first child each time, which made every
   * frame quadratic on long documents); nodeDOM only as a fallback. Measurements are cached per
   * element and PM node, so unchanged blocks are not read again.
   * @param {HTMLElement} container - Element holding the children's DOM.
   * @param {object} containerDesc - View desc owning `container` (view.docView at the top).
   * @param {import('@tiptap/pm/model').Node} parent - Node whose children these are.
   * @param {number} contentStart - Document position of `parent`'s content start.
   * @param {number} depth - Wrapper nesting depth (0 at the top level).
   * @param {boolean} items - The children are list items: each one is a unit, never descended.
   * @param {{leafEls: Element[], widgetEls: Element[]}} out - Collects the measured unit elements
   *   (to observe) and the pagination widgets found on the way (to repaint).
   * @returns {Array<object>} Tree nodes in order.
   */
  collect(container, containerDesc, parent, contentStart, depth, items, out) {
    const { view } = this;
    const { options } = this.shared;
    const breakName = options.breakNodeName;
    const transparent = options.transparentNodes || PAGINATION_TRANSPARENT_NODES;
    const lists = options.listNodes || PAGINATION_LIST_NODES;
    const children = Array.from(container.children);
    const nested = container !== view.dom;
    const entries = [];
    let cursor = 0;
    parent.forEach((node, offset) => {
      const pos = contentStart + offset;
      let el = null;
      for (let i = cursor; i < children.length; i += 1) {
        const desc = children[i].pmViewDesc;
        if (!desc || desc.parent !== containerDesc || !desc.node) continue; // widget or foreign element
        if (desc.node === node) {
          el = children[i];
          cursor = i + 1;
        }
        break;
      }
      if (!el) {
        try {
          el = view.nodeDOM(pos);
        } catch {
          el = null;
        }
        if (el && el.parentNode !== container) el = this.childAncestor(el, container);
        if (el) {
          const at = children.indexOf(el, cursor);
          if (at >= 0) cursor = at + 1;
        }
      }
      entries.push({ node, pos, el });
    });

    // In-flow elements other plugins put between blocks count toward the next block; the
    // pagination widgets are only noted (they are what this engine sizes).
    const extra = new Array(entries.length).fill(0);
    const indexOf = new Map();
    entries.forEach((entry, i) => entry.el && indexOf.set(entry.el, i));
    let nextIndex = 0;
    children.forEach((el) => {
      if (indexOf.has(el)) {
        nextIndex = indexOf.get(el) + 1;
        return;
      }
      if (el.hasAttribute(WIDGET_ATTR)) {
        out.widgetEls.push(el);
        return;
      }
      if (el.classList.contains(PAGINATION_CLASSES.spacer) || el.classList.contains(PAGINATION_CLASSES.tail)) return;
      const style = styleOf(el);
      if (outOfFlow(style)) return;
      const box = measureBlock(el);
      const target = Math.min(nextIndex, entries.length - 1);
      if (target >= 0) extra[target] += box.height + Math.max(0, box.marginTop) + Math.max(0, box.marginBottom);
    });

    return entries.map((entry, i) => {
      const { node, pos, el } = entry;
      const at = { pos, container: nested ? container : null };
      const name = node.type.name;
      const isList = lists.includes(name);
      if (!items && el && depth < MAX_NEST_DEPTH && (isList || transparent.includes(name))) {
        const desc = el.pmViewDesc;
        let wrap = this.wrapCache.get(el);
        if (!wrap || wrap.node !== node || (wrap.host && !el.contains(wrap.host))) {
          const host = desc && desc.node === node ? contentHost(desc) : null;
          wrap = { node, host, box: host ? wrapperBox(el) : null };
          this.wrapCache.set(el, wrap);
        }
        if (wrap.host) {
          const kids = this.collect(wrap.host, desc, node, pos + 1, depth + 1, isList, out);
          if (kids.length) return { ...wrap.box, children: kids, at, extraBefore: extra[i] };
        }
      }
      let box = el ? this.cache.get(el) : null;
      if (!box || box.node !== node) {
        box = { node, ...measureBlock(el) };
        if (el) this.cache.set(el, box);
      }
      if (el) out.leafEls.push(el);
      return {
        height: box.height,
        marginTop: box.marginTop,
        marginBottom: box.marginBottom,
        extraBefore: extra[i],
        isBreak: name === breakName,
        // Print keeps a heading with the block after it (page-break-after: avoid), Word too.
        keepWithNext: name === 'heading',
        // The empty paragraph the editor keeps at the end of the document (see computePageLayout).
        trailingEmpty: !nested && i === entries.length - 1 && i > 0 && node.isTextblock && node.content.size === 0,
        at,
      };
    });
  }

  /**
   * Request a measurement on the next animation frame (coalesced).
   * @param {{fromDoc?: boolean}} [opts] - Whether a doc change caused it.
   * @returns {void}
   */
  schedule(opts = {}) {
    if (opts.fromDoc) this.fromDoc = true;
    if (this.frame || !this.attached) return;
    this.frame = requestFrame(() => {
      this.frame = null;
      this.measure();
    });
  }

  /** Measure, lay out, and update widgets. @returns {void} */
  measure() {
    const { view } = this;
    if (!this.attached || view.isDestroyed) return;
    const pluginState = this.pluginState;
    if (!pluginState || !pluginState.config.enabled) return;
    if (view.composing) {
      clearTimeout(this.retry);
      this.retry = setTimeout(() => this.schedule(), COMPOSITION_RETRY_MS);
      return;
    }
    const { dom } = view;
    if (!dom.isConnected) return;
    const fromDoc = this.fromDoc;
    this.fromDoc = false;

    const rootStyle = styleOf(dom);
    const width = dom.clientWidth;
    if (width !== this.width) {
      this.invalidateAll();
      this.width = width;
    }
    const paper = resolvePaper(pluginState.config.paperSize);
    const hasPadding = rootStyle && (px(rootStyle.paddingTop) || px(rootStyle.paddingBottom));
    const marginTop = hasPadding ? px(rootStyle.paddingTop) : DEFAULT_MARGIN;
    const marginBottom = hasPadding ? px(rootStyle.paddingBottom) : DEFAULT_MARGIN;
    const marginLeft = rootStyle ? px(rootStyle.paddingLeft) : DEFAULT_MARGIN;
    const marginRight = rootStyle ? px(rootStyle.paddingRight) : DEFAULT_MARGIN;
    const cssGap = rootStyle && typeof rootStyle.getPropertyValue === 'function'
      ? parseFloat(rootStyle.getPropertyValue('--q-page-gap')) : NaN;
    const gap = Number.isFinite(cssGap) && cssGap >= 0 ? cssGap : size(this.shared.options.gap);
    const pageContentHeight = Math.max(MIN_CONTENT_HEIGHT, paper.height - marginTop - marginBottom);

    // The block tree (top-level blocks, and the children of the wrappers and lists the page may
    // end inside), flattened into the units the layout places.
    const { doc } = view.state;
    const collected = { leafEls: [], widgetEls: [] };
    const tree = this.collect(dom, view.docView, doc, 0, 0, false, collected);
    const units = flattenPageBlocks(tree);
    this.observeBlocks(collected.leafEls);

    const layout = computePageLayout({
      blocks: units,
      pageContentHeight,
      chromeHeight: marginBottom + gap + marginTop,
      pageMarginTop: marginTop,
      // A final empty paragraph may reach this far into the bottom margin (clear of the number).
      trailingAllowance: marginBottom / 2,
    });
    const total = layout.pageCount;
    const format = pluginState.config.pageNumberFormat;
    const common = { total, format, marginTop, marginBottom, marginLeft, marginRight, gap };
    const atOf = (index) => (index < units.length && units[index].at ? units[index].at : { pos: doc.content.size, container: null });
    // How far a widget inside a wrapper sits from the sheet edges: the content box of the element
    // it is rendered in (read once per element, before any widget is repainted).
    let rootRect = null;
    const insets = new Map();
    const insetsOf = (container) => {
      if (!container) return { insetLeft: marginLeft, insetRight: marginRight };
      if (insets.has(container)) return insets.get(container);
      if (!rootRect) rootRect = dom.getBoundingClientRect();
      const rect = container.getBoundingClientRect();
      const cs = styleOf(container);
      const side = (Side) => px(cs && cs[`padding${Side}`]) + px(cs && cs[`border${Side}Width`]);
      const value = {
        insetLeft: rect.left + side('Left') - rootRect.left,
        insetRight: rootRect.right - (rect.right - side('Right')),
      };
      insets.set(container, value);
      return value;
    };
    const widgets = layout.spacers.map((spacer) => {
      const at = atOf(spacer.beforeIndex);
      return {
        key: `q-page-end-${spacer.pageNumberEnded}`,
        kind: 'spacer',
        pos: at.pos,
        data: {
          ...common,
          ...insetsOf(at.container),
          nested: Boolean(at.container),
          kind: 'spacer',
          height: spacer.height,
          fill: spacer.fill,
          shift: spacer.shift,
          forced: spacer.forced,
          page: spacer.pageNumberEnded,
          firstPage: spacer.pageNumberStarted,
        },
      };
    });
    widgets.push({
      key: 'q-page-tail',
      kind: 'tail',
      pos: doc.content.size,
      data: {
        ...common,
        kind: 'tail',
        height: Math.max(0, layout.lastPageFill),
        fill: layout.lastPageFill,
        page: total,
        firstPage: layout.lastSheetFirstPage,
      },
    });

    const positionSignature = widgets.map((w) => `${w.key}@${w.pos}`).sort().join('|');
    // Compare with where the current widgets actually sit (they are mapped through every doc
    // change), not with the last dispatched positions: typing shifts every later position, and
    // re-dispatching the same layout on each keystroke would add a transaction per key.
    const placedSignature = pluginState.decorations
      .find()
      .map((deco) => `${deco.spec.key}@${deco.from}`)
      .sort()
      .join('|');
    const placedMatches = positionSignature === placedSignature;
    // The widgets in the DOM are the ones this layout keeps: snap their heights to the real
    // layout (sub-pixel margins the math cannot know), so sheets never drift over many pages.
    if (placedMatches) {
      alignToSheets(widgets, collected.widgetEls, { dom, paperHeight: paper.height, gap, marginTop, marginBottom });
    }

    // Fresh data first: toDOM (new widgets) and the in-place repaint both read it. The live
    // widgets are found in the DOM, not remembered from toDOM: ProseMirror reuses widget DOM by
    // key (also across a plugin reconfigure, which recreates this controller), so toDOM is not
    // called again for them.
    const { data, storage } = this.shared;
    data.clear();
    widgets.forEach((w) => data.set(w.key, w.data));
    collected.widgetEls.forEach((el) => {
      const key = el.getAttribute(WIDGET_KEY_ATTR);
      if (key && data.has(key)) paintWidget(el, data.get(key));
    });

    const countChanged = storage && storage.pageCount !== total;
    if (storage) storage.pageCount = total;
    if (placedMatches && !countChanged) {
      this.positionSignature = positionSignature;
      this.childCount = dom.childElementCount;
      this.aligning = false;
      return;
    }
    this.positionSignature = positionSignature;

    const tr = view.state.tr
      .setMeta(paginationPluginKey, { layout: widgets.map(({ key, kind, pos }) => ({ key, kind, pos })) })
      .setMeta('addToHistory', false);
    // A block that moved to the next page while typing takes the caret with it: keep it visible.
    if (fromDoc && view.hasFocus()) tr.scrollIntoView();
    this.dispatching = true;
    try {
      view.dispatch(tr);
    } finally {
      this.dispatching = false;
    }
    this.childCount = dom.childElementCount;
    // New widgets are drawn from the math; one more frame aligns them to the real layout (once:
    // that frame keeps the same page ends, so it repaints in place and dispatches nothing).
    if (!placedMatches && !this.aligning) {
      this.aligning = true;
      this.schedule();
    }
  }

  /**
   * Keep the ResizeObserver on exactly the currently measured blocks (top-level blocks and the
   * children of split wrappers; never a wrapper, whose height includes the spacers in it).
   * @param {Element[]} elements - Measured block elements.
   * @returns {void}
   */
  observeBlocks(elements) {
    const ro = this.resizeObserver;
    if (!ro) return;
    const current = new Set();
    elements.forEach((el) => {
      if (!el || el.nodeType !== 1) return;
      current.add(el);
      if (!this.observed.has(el)) ro.observe(el);
    });
    this.observed.forEach((el) => {
      if (!current.has(el)) ro.unobserve(el);
    });
    this.observed = current;
  }

  /** Plugin view destroy. @returns {void} */
  destroy() {
    this.destroyed = true;
    this.detach();
  }
}

/* ------------------------------------------------------------------------------------------ */
/* Extension                                                                                    */
/* ------------------------------------------------------------------------------------------ */

/**
 * Caret for a click on a page that holds no text: the page after a page break that is followed
 * straight away by another break (or by the end of the document). Such a page has no position
 * inside a textblock, so a text caret would land on a neighboring sheet; a gap cursor sits on the
 * clicked page instead (typing there inserts a paragraph, the Gapcursor extension's behavior).
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {number} pos - Widget position (between two top-level blocks).
 * @param {-1|0|1} dir - Which page was clicked: 1 the page that starts at `pos` (a spacer's lower
 *   part), -1 the page that ends at `pos` (a spacer's upper part), 0 the last page (the tail).
 * @param {string} breakName - Page break node name.
 * @returns {GapCursor|null} Gap cursor on that page, or null when the page has text (or none fits).
 */
export const emptyPageCursor = (doc, pos, dir, breakName) => {
  if (pos < 0 || pos > doc.content.size) return null;
  const $pos = doc.resolve(pos);
  if ($pos.depth !== 0) return null;
  const isBreak = (node) => Boolean(node && node.type.name === breakName);
  let at = null;
  if (dir > 0 || dir === 0) {
    // The page starting here (or the last page) is empty when a break or the end comes next.
    const startsEmpty = dir > 0 ? (!$pos.nodeAfter || isBreak($pos.nodeAfter)) : isBreak($pos.nodeBefore);
    if (startsEmpty) at = pos;
  } else if (isBreak($pos.nodeBefore)) {
    // The page ending here ends with a break; it is empty when nothing but a page end precedes
    // that break (GapCursor.valid rejects a text block before it).
    at = pos - $pos.nodeBefore.nodeSize;
  }
  if (at === null) return null;
  const $at = doc.resolve(at);
  return GapCursor.valid($at) ? new GapCursor($at) : null;
};

/**
 * Build the widget decorations for a layout.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {Array<{key: string, kind: string, pos: number}>} widgets - Widgets.
 * @param {(key: string, kind: string) => Function} toDOMFor - Widget DOM factory.
 * @returns {DecorationSet} Decorations.
 */
const buildDecorations = (doc, widgets, toDOMFor) => {
  const max = doc.content.size;
  const decos = widgets
    .filter((w) => w.pos >= 0 && w.pos <= max)
    .map((w) => Decoration.widget(w.pos, toDOMFor(w.key, w.kind), {
      key: w.key,
      side: w.kind === 'tail' ? 1 : -1,
      ignoreSelection: true,
      stopEvent: (event) => !DRAG_EVENTS.has(event.type),
      marks: [],
    }));
  return DecorationSet.create(doc, decos);
};

const Pagination = Extension.create({
  name: 'pagination',

  addOptions() {
    return {
      enabled: false,
      paperSize: 'letter',
      pageNumberFormat: 'n',
      gap: 24,
      breakNodeName: 'pageBreak',
      transparentNodes: PAGINATION_TRANSPARENT_NODES,
      listNodes: PAGINATION_LIST_NODES,
    };
  },

  addStorage() {
    return { enabled: false, paperSize: 'letter', pageCount: 0 };
  },

  addCommands() {
    return {
      setPagination: (options = {}) => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(paginationPluginKey, { config: options || {} });
          tr.setMeta('addToHistory', false);
        }
        return true;
      },
      refreshPagination: () => ({ tr, dispatch }) => {
        if (dispatch) {
          tr.setMeta(paginationPluginKey, { refresh: true });
          tr.setMeta('addToHistory', false);
        }
        return true;
      },
    };
  },

  addProseMirrorPlugins() {
    const { options, storage } = this;
    /** key -> latest widget data from the last measurement. */
    const data = new Map();
    /**
     * `committed` holds the last settings the view applied. A fresh EditorState (content load,
     * review entered or reopened) starts from it instead of the options, so the Pages view
     * survives `view.updateState()`.
     */
    const shared = { data, storage, options, committed: null };

    const toDOMFor = (key, kind) => (view, getPos) => {
      const doc = view.dom.ownerDocument;
      const el = doc.createElement('div');
      el.className = kind === 'tail' ? PAGINATION_CLASSES.tail : PAGINATION_CLASSES.spacer;
      el.setAttribute('contenteditable', 'false');
      el.setAttribute('aria-hidden', 'true');
      el.setAttribute(WIDGET_ATTR, kind);
      el.setAttribute(WIDGET_KEY_ATTR, key);
      el.draggable = false;
      el.style.margin = '0';
      el.style.padding = '0';
      el.style.border = '0';
      if (kind !== 'tail') {
        child(el, PAGINATION_CLASSES.end);
        child(el, PAGINATION_CLASSES.gap);
        child(el, PAGINATION_CLASSES.start);
      }
      child(el, PAGINATION_CLASSES.number);
      el.addEventListener('mousedown', (event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        if (!view.editable || view.isDestroyed) return;
        const pos = typeof getPos === 'function' ? getPos() : null;
        if (typeof pos !== 'number') return;
        const info = data.get(key);
        const rect = el.getBoundingClientRect();
        const nextPageFrom = rect.bottom - (info ? info.marginTop + info.gap : 0);
        const dir = kind === 'tail' || event.clientY < nextPageFrom ? -1 : 1;
        try {
          // A page with no text (between two page breaks, or after a final one) gets a gap
          // cursor on that page; typing there adds a paragraph to it, like Word's paragraph mark.
          // Otherwise prefer a caret in text (end of this page, start of the next): a page break
          // or other atom next to the widget must not end up node-selected, where typing would
          // replace it.
          const $pos = view.state.doc.resolve(pos);
          const selection = emptyPageCursor(view.state.doc, pos, kind === 'tail' ? 0 : dir, options.breakNodeName)
            || Selection.findFrom($pos, dir, true)
            || Selection.findFrom($pos, -dir, true)
            || Selection.near($pos, dir);
          view.dispatch(view.state.tr.setSelection(selection).scrollIntoView());
        } catch {
          /* position no longer valid */
        }
        view.focus();
      });
      paintWidget(el, data.get(key));
      return el;
    };

    const initialConfig = normalizeConfig(
      { enabled: false, paperSize: 'letter', pageNumberFormat: 'n', refresh: 0 },
      { enabled: options.enabled, paperSize: options.paperSize, pageNumberFormat: options.pageNumberFormat },
    );

    return [
      new Plugin({
        key: paginationPluginKey,
        state: {
          init: () => ({
            config: shared.committed ? normalizeConfig(initialConfig, shared.committed) : initialConfig,
            decorations: DecorationSet.empty,
          }),
          apply: (tr, prev) => {
            const meta = tr.getMeta(paginationPluginKey);
            let { config, decorations } = prev;
            if (meta && meta.config) config = normalizeConfig(config, meta.config);
            if (meta && meta.refresh) config = { ...config, refresh: (config.refresh || 0) + 1 };
            if (!config.enabled) return { config, decorations: DecorationSet.empty };
            if (meta && meta.layout) {
              decorations = buildDecorations(tr.doc, meta.layout, toDOMFor);
            } else if (tr.docChanged) {
              decorations = decorations.map(tr.mapping, tr.doc);
            }
            return config === prev.config && decorations === prev.decorations ? prev : { config, decorations };
          },
        },
        props: {
          decorations: (state) => paginationPluginKey.getState(state).decorations,
          attributes: (state) => {
            const { config } = paginationPluginKey.getState(state);
            if (!config.enabled) return {};
            return { class: PAGINATION_CLASSES.root, 'data-paper-size': resolvePaper(config.paperSize).id };
          },
        },
        view: (view) => {
          const controller = new PaginationController(view, shared);
          return {
            update: (v, prevState) => controller.update(v, prevState),
            destroy: () => controller.destroy(),
          };
        },
      }),
    ];
  },
});

export default Pagination;
